<template>
  <div>
    <TButton
      v-bind="$attrs"
      outline
     
      :disabled="loading || isLoading"
      @click="openFileBrowserWindow"
    >
      <AtomSpinner
        v-if="loading || isLoading"
        size="sm"
      />
      <TIcon
        v-else
        :icon="['far', 'camera']"
      />
      <span class="ml-1">{{ isChangePhoto ? t('photosManager.changePhoto') : t('photosManager.addPhoto') }}</span>
    </TButton>

    <input
      ref="uppyInputEl"
      type="file"
     
      hidden
      :accept="allowedFileTypes?.join(',')"
      @change="addLocalFiles"
    >
  </div>
</template>

<script setup lang="ts">
import { PINTURA_CROP_ASPECT_RATIOS } from '~/constants/pintura'
import type { Nullable } from '~/types'

defineProps<{
  isChangePhoto?: boolean
  isLoading?: boolean
}>()

const emit = defineEmits<{
  'avatar-upload-failure': [boolean]
  'avatar-upload-success': [string]
}>()

defineExpose({ openFileBrowserWindow })

const { t } = useI18n()

const loading = ref(false)

const newImageKey = ref<Nullable<string | undefined>>()

const { uppy, allowedFileTypes, addLocalFiles } = useUppy({
  awsS3: {
    getUploadParams: async (params: { fileName: string, fileType: string, fileExt: string }) => {
      try {
        const { ObjectKey, UploadUrl } = await $rentals('/api/photos/generate-pre-signed-url', {
          query: {
            ext: params.fileExt,
            type: 'Avatar',
          },
        })

        newImageKey.value = ObjectKey

        return {
          ObjectKey,
          UploadUrl,
        }
      }
      catch (err) {
        emit('avatar-upload-failure', true)
        throw err
      }
    },
  },
  attachEditor: true,
  dokaOptions: {
    imageCropAspectRatio: PINTURA_CROP_ASPECT_RATIOS.Square,
    maxSize: 500,
  },
})

uppy.on('upload', () => (loading.value = true))
uppy.on('upload-success', onUploadSuccess)
uppy.on('upload-error', () => {
  loading.value = false
  emit('avatar-upload-failure', true)
})

const { $rentals, $captureError } = useNuxtApp()

const uppyInputEl = useTemplateRef<HTMLInputElement>('uppyInputEl')

function openFileBrowserWindow() {
  uppyInputEl.value?.click()
}

async function onUploadSuccess() {
  if (!newImageKey.value) {
    return
  }

  try {
    await $rentals('/api/user-profiles/me/avatar', {
      method: 'PUT',
      body: {
        Avatar: newImageKey.value,
      },
    })

    emit('avatar-upload-success', newImageKey.value)
  }
  catch (err) {
    $captureError(err)
  }
  finally {
    loading.value = false
  }
}
</script>

<i18n src="~~/i18n/locales/common/photos-manager.json" lang="json" />
